import React, { useEffect, useRef, useState } from 'react';
import { ClipboardIcon, CheckIcon, BoltIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

// Step 2 — where the agent's mailbox lives. Default is the instant platform
// subdomain (zero DNS); "own domain" walks add → DNS records → verify with
// polling. Both paths mark the step server-side so resume works.
const StepWorkspace = ({ status, showNotification, markProgress, onDone, onBack, reloadStatus }) => {
  const [mode, setMode] = useState(status.workspaceMode || (status.hasVerifiedDomain ? 'domain' : 'instant'));
  const [domainName, setDomainName] = useState('');
  const [domain, setDomain] = useState(null); // { id, dnsRecords[] } after add
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(status.hasVerifiedDomain);
  const [copied, setCopied] = useState('');
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const copy = (key, value) => {
    navigator.clipboard?.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const addDomain = async () => {
    const name = domainName.trim().toLowerCase();
    if (!name || adding) return;
    setAdding(true);
    try {
      const res = await axiosInstance.post('/api/business/domains/add', { domainName: name });
      const d = res.data?.domain || res.data?.data || res.data;
      setDomain({ id: d.id || d._id, domainName: name, dnsRecords: d.dnsRecords || [] });
      startPolling(d.id || d._id);
    } catch (error) {
      logger.error('Add domain failed:', error);
      showNotification?.(error.response?.data?.message || 'Could not add that domain', 'error');
    } finally {
      setAdding(false);
    }
  };

  const checkVerification = async (domainId) => {
    try {
      await axiosInstance.post('/api/business/domains/verify', { domainId });
      const list = await axiosInstance.get('/api/business/domains');
      const rows = list.data?.domains || list.data?.data || [];
      const row = rows.find((r) => String(r.id || r._id) === String(domainId));
      if (row?.isVerified) {
        clearInterval(pollRef.current);
        setVerified(true);
        showNotification?.('Domain verified!', 'success');
        return true;
      }
    } catch (error) {
      logger.error('Domain verify poll failed:', error);
    }
    return false;
  };

  const startPolling = (domainId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => checkVerification(domainId), 20000);
  };

  const verifyNow = async () => {
    if (!domain || verifying) return;
    setVerifying(true);
    const ok = await checkVerification(domain.id);
    if (!ok) showNotification?.('Not verified yet — DNS changes can take a few minutes to propagate', 'info');
    setVerifying(false);
  };

  const finish = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (!await markProgress('workspace', { workspaceMode: mode })) return;
      await reloadStatus();
      await onDone({ workspaceMode: mode });
    } catch (error) {
      logger.error('Workspace selection failed:', error);
    } finally { setSaving(false); }
  };

  const cardCls = (active) =>
    `flex-1 text-left border rounded-xl p-4 transition-colors ${
      active
        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10'
        : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
    }`;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Choose your email domain</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Choose an Aiinak-provided address or connect a domain you own. Individuals and teams can use either option.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button className={cardCls(mode === 'instant')} onClick={() => setMode('instant')} aria-pressed={mode === 'instant'}>
          <div className="flex items-center gap-2 mb-1">
            <BoltIcon className="w-5 h-5 text-orange-500" />
            <span className="font-medium text-gray-900 dark:text-white text-sm">I don’t have a domain</span>
            <span className="text-[10px] uppercase tracking-wide bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded">{status.platformMail?.enabled ? 'No domain needed' : 'Unavailable here'}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use aiinakmail.com with a subdomain for your workspace. Example:{' '}
            <span className="font-mono text-gray-700 dark:text-gray-300">name@{status.platformMail?.suggestedDomain || 'your-workspace.aiinakmail.com'}</span>
          </p>
        </button>
        <button className={cardCls(mode === 'domain')} onClick={() => setMode('domain')}>
          <div className="flex items-center gap-2 mb-1">
            <GlobeAltIcon className="w-5 h-5 text-orange-500" />
            <span className="font-medium text-gray-900 dark:text-white text-sm">Use your own domain</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use your existing brand and a mailbox on a verified domain.
          </p>
        </button>
      </div>

      {mode === 'instant' && !status.platformMail?.enabled && (
        <p role="status" className="text-sm text-gray-600 dark:text-gray-300 mb-6">
          Aiinak-provided addresses are not enabled here yet. Your progress is saved; return when available or connect your own domain.
        </p>
      )}
      {mode === 'domain' && !verified && (
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 mb-6">
          {!domain ? (
            <div className="flex gap-2">
              <input
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="yourcompany.com"
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addDomain()}
              />
              <button
                onClick={addDomain}
                disabled={adding || !domainName.trim()}
                className="px-4 py-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium disabled:opacity-50"
              >
                {adding ? 'Adding…' : 'Add domain'}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                Add these records at your DNS provider for <span className="font-mono">{domain.domainName}</span>.
                We re-check automatically every 20 seconds.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400">
                      <th className="py-1 pr-3">Type</th>
                      <th className="py-1 pr-3">Host</th>
                      <th className="py-1 pr-3">Value</th>
                      <th className="py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {(domain.dnsRecords || []).map((r, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-800 align-top">
                        <td className="py-2 pr-3 font-mono text-gray-900 dark:text-white">{r.type}</td>
                        <td className="py-2 pr-3 font-mono text-gray-700 dark:text-gray-300">{r.name || '@'}</td>
                        <td className="py-2 pr-3 font-mono text-gray-700 dark:text-gray-300 break-all max-w-[280px]">{r.value}</td>
                        <td className="py-2">
                          <button onClick={() => copy(`r${i}`, r.value)} className="text-gray-400 hover:text-orange-500">
                            {copied === `r${i}` ? <CheckIcon className="w-4 h-4 text-green-500" /> : <ClipboardIcon className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={verifyNow}
                disabled={verifying}
                className="mt-3 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {verifying ? 'Checking…' : 'Check now'}
              </button>
            </div>
          )}
        </div>
      )}

      {mode === 'domain' && verified && (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 mb-6">
          <CheckIcon className="w-5 h-5" /> Domain verified — ready for your people and agents.
        </div>
      )}

      <div className="flex justify-between mt-2">
        {onBack ? (
          <button onClick={onBack} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">Back</button>
        ) : <span />}
        <div className="flex gap-2">
          <button
            onClick={finish}
            disabled={saving || (mode === 'domain' ? !verified : !status.platformMail?.enabled)}
            className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium"
          >
            {saving ? 'Preparing your domain…' : mode === 'instant' ? 'Use Aiinak domain & continue' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepWorkspace;
